import React, { useEffect, useState } from 'react';
import { rememberUpsellToken } from '../../../../../utils/upsellToken';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  FormControl,
  FormLabel,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Link,
  Chip
} from '@mui/material';
import { stripePromise } from '../../../../../utils/stripe';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import { ErrorOutline } from '@mui/icons-material';
import CircleIcon from '@mui/icons-material/Circle';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import CountrySelect from '../../../../../components/CountrySelect/CountrySelect';
import { emailValidationRegex } from '../../../../../constants/regex';
import { SignUpFormType } from '../../../../../types/types';
import { useNetwork } from '../../../../../utils/network';
import { publicIp } from 'public-ip';
import { useShowSnackbar } from '../../../../../utils/snackbar';
import { validateNameValue } from '../../../../../utils/nameValidation';
import ExclusiveOfferInjected from '../../../../MemberPortal/Giveaway/components/UpsellModal';
import { useNavigate } from 'react-router-dom';
// Stripe.js instance is the shared toggle-aware one imported above (utils/stripe).

interface Package {
  id: number;
  name: string;
  imageUrl: string;
  price: number;
  cards: number;
  entries: number;
  PackageRarities: {
    rarity: string;
    guaranteed: number;
    pullable: true;
  }[];
  Bonus?: {
    description: string;
    extraCards: number;
    extraEntries: number;
    price: number;
  };
}

interface CheckoutProps {
  type: 'standalone' | 'subscription' | null;
  name: string | null;
}

const Checkout: React.FC<CheckoutProps> = ({ type, name }) => {
  const stripe = useStripe();
  const navigate = useNavigate();
  const elements = useElements();
  const { post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const { state } = useLocation();
  const [packageData, setPackageData] = useState<Package | null>(null);
  const [fetching, setFetching] = useState<boolean>(true);
  const [miscError, setMiscError] = useState<string | null>(null);
  const [enableBonus, setEnableBonus] = useState<boolean>(false);
  const [message, setMessage] = useState('');
  const [selectedAreaCode, setSelectedAreaCode] = useState<string>('');
  const [isGiveawayEligible, setIsGiveawayEligible] = useState<boolean | null>(
    null
  );
  const [hasReadTermsAndConditions, setHasReadTermsAndConditions] =
    useState<boolean>(false);
  const [acceptTnC, setAcceptTnC] = useState(false);
  const [doesEmailExist, setDoesEmailExist] = useState<boolean | null>(null);
  const [isUsernameValid, setIsUsernameValid] = useState<boolean | null>(null);
  const [showFinalUpsell, setShowFinalUpsell] = useState<boolean>(false);
  const [showThankYou, setShowThankYou] = useState<boolean>(false);
  const [stripeLoading, setStripeLoading] = useState<boolean>(false);
  const [paymentIntentIdAfterPayment, setPaymentIntentIdAfterPayment] =
    useState<string>('');

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isValid, errors },
    clearErrors
  } = useForm<SignUpFormType>({
    mode: 'onChange',
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      username: '',
      country: undefined,
      state: '',
      phone: '',
      referralCode: ''
    }
  });

  const onSubmit: SubmitHandler<SignUpFormType> = async (data) => {
    if (!stripe || !elements) {
      setMessage(
        'Stripe has not loaded. Please refresh the page and try again.'
      );
      return;
    }

    const returnUrl = window.location.href;

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setMessage(submitError.message || 'Failed to submit payment details.');
      setStripeLoading(false);
      return;
    }

    setStripeLoading(true);

    const attemptId = crypto.randomUUID();

    try {
      if (type === 'subscription') {
        // --- Subscription + NEW card ---
        // 1) Precheck SI (trial_precheck) to get a PM
        const si = await post(
          '/checkout/create-trial-setup-intent',
          {
            username: data.username,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            country: data.country.label,
            isGiveawayEligible: false,
            phone: selectedAreaCode + data.phone,
            state: data.state,
            referralCode: data.referralCode
          },
          true
        );

        const { error: setupErr, setupIntent } = await stripe.confirmSetup({
          elements,
          clientSecret: si.clientSecret,
          confirmParams: { return_url: returnUrl },
          redirect: 'if_required'
        });
        if (setupErr) {
          showSnackbar(setupErr.message || 'Card setup failed.', 'error');
          setStripeLoading(false);
          return;
        }

        const pmId = setupIntent.payment_method as string;

        // 2) Create subscription using that PM
        const resData = await post(
          '/checkout/create-subscription-payment-intent',
          {
            packageId: packageData ? packageData.id : '',
            hasBonus: false,
            email: data.email,
            username: data.username,
            firstName: data.firstName,
            lastName: data.lastName,
            country: data.country.label,
            isGiveawayEligible,
            phone: selectedAreaCode + data.phone,
            state: data.state,
            referralCode: data.referralCode,
            ipAddress: await publicIp(),
            paymentMethodId: pmId
          },
          true
        );

        const secret = resData?.clientSecret ?? null;

        // if (!secret) {
        //   // Trial created; nothing to confirm now.
        //   showSnackbar('Your free trial has started!', 'success');
        //   await getCurrentSubscriptionAndCloseModal();
        //   return;
        // }

        /* A null clientSecret used to reach .startsWith and throw a raw
         * TypeError, which the catch below now shows to the buyer verbatim. */
        if (!secret || typeof secret !== 'string') {
          showSnackbar(
            'We could not start this payment. Please try again.',
            'error'
          );
          return;
        }

        if (secret.startsWith('pi_')) {
          await elements.submit();
          const { error } = await stripe.confirmPayment({
            elements,
            clientSecret: secret,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required'
          });
          if (error) {
            showSnackbar(
              error.message || 'Payment confirmation failed',
              'error'
            );
            return;
          }
        } else if (secret.startsWith('seti_')) {
          await elements.submit();
          const { error } = await stripe.confirmSetup({
            elements,
            clientSecret: secret,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required'
          });
          if (error) {
            showSnackbar(error.message || 'Card setup failed', 'error');
            return;
          }
        }

        setShowThankYou(true);

        showSnackbar(
          'Account created successfully! Please check your email for verification.',
          'success'
        );
        return;
      } else {
        const resData = await post(`/checkout/create-${type}-payment-intent`, {
          packageId: packageData ? packageData.id : '',
          hasBonus: enableBonus,
          username: data.username,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          phone: selectedAreaCode + data.phone,
          country: data.country.label,
          state: data.state,
          referralCode: data.referralCode,
          ipAddress: await publicIp(),
          paymentMethodId: null,
          isGiveawayEligible,
          attemptId
        });

        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          clientSecret: resData.clientSecret,
          confirmParams: {
            return_url: `${window.location}`
          },
          redirect: 'if_required'
        });

        if (error) {
          showSnackbar(
            error.message || 'An unexpected error occurred.',
            'error'
          );
        } else {
          if (type === 'standalone') {
            if (![10, 31, 32].includes(packageData?.id ?? -1)) {
              // if (!!enableBonus) {
              // keep the token that authorises the upsell charge for this purchase
              const canUpsell = rememberUpsellToken(
                paymentIntent.id,
                resData?.upsellToken
              );
              setPaymentIntentIdAfterPayment(paymentIntent.id);
              // No token, no offer — see utils/upsellToken. The purchase itself
              // succeeded either way, so this goes straight to the thank-you.
              if (canUpsell) setShowFinalUpsell(true);
              else setShowThankYou(true);
              // } else {
              //   setShowThankYou(true);
              // }
            } else {
              setShowThankYou(true);
            }
          } else {
            setShowThankYou(true);
          }
        }

        showSnackbar(
          'Account created successfully! Please check your email for verification.',
          'success'
        );
      }
    } catch (error: any) {
      /* useNetwork throws a plain Error carrying the server's message (see
       * utils/network.ts) — never an axios error, so there is no `.response` to
       * reach through. Reading one threw a TypeError inside this catch, which meant
       * a failed checkout showed NO message at all and left the button spinning. */
      showSnackbar(
        error?.message || 'An unexpected error occurred. Please try again.',
        'error'
      );
    } finally {
      // Whatever happened, the buyer gets their button back.
      setStripeLoading(false);
    }
  };

  useEffect(() => {
    if (!type || !name) {
      setMiscError('Invalid query parameters');
      setFetching(false);
      return;
    }

    const fetchData = async () => {
      setFetching(true);
      setMiscError(null);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/package/${type}?name=${name}`
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch ${type}`);
        }
        const data = await response.json();
        setPackageData(data);
      } catch (error) {
        setMiscError(
          error instanceof Error ? error.message : 'An unknown error occurred'
        );
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [type, name]);

  if (fetching) {
    return <CircularProgress />;
  }

  if (miscError) {
    return <Typography color="error">{miscError}</Typography>;
  }

  if (!packageData) {
    return <Typography color="error">{type} not found</Typography>;
  }

  const isStandalone = (data: any): data is Package => type === 'standalone';

  const totalAmount =
    isStandalone(packageData) && packageData.Bonus && enableBonus
      ? (Number(packageData.price) + Number(packageData.Bonus.price)).toFixed(2)
      : packageData.price;

  /* Advances the form; asks the server nothing.
   *
   * This used to POST /auth/check-email and, if the address was ours, replace the
   * whole purchase with "Email already exists, please login to purchase packs."
   * Anyone could read that by typing an address, and it blocked a returning buyer
   * from a purchase they were entitled to make.
   *
   * Everyone now fills the same fields. The server keys on the email alone and
   * applies the rest only as defaults for a genuinely new account, so nothing an
   * existing member re-types can overwrite or reject their record. */
  const checkEmail = async (_email: string) => {
    setDoesEmailExist(false);
  };

  /* Advances to payment without asking the server anything.
   *
   * This used to POST /auth/check-username and refuse to continue when the name was
   * taken — the same oracle as the email check above, and the same trap: a returning
   * member's OWN username is taken, so they could never get past this step without
   * inventing a name they did not want and would not keep.
   *
   * The server decides what happens to it: an existing account is matched by email
   * and ignores the field entirely, and a new account drops a name somebody already
   * has (ensureCustomerAndUser) rather than failing the purchase over it — the
   * member portal then asks for a free one at first sign-in. */
  const advanceToPayment = () => {
    clearErrors('username');
    setIsUsernameValid(true);
  };

  const goBack = () => {
    // React Router sets a history index (idx) in v6.4+ for BrowserRouter
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/', { replace: true });
    }
  };

  const defaultRarities = [
    { rarity: 'common', guaranteed: false, pullable: false },
    { rarity: 'rare', guaranteed: false, pullable: false },
    { rarity: 'epic', guaranteed: false, pullable: false },
    { rarity: 'legendary', guaranteed: false, pullable: false },
    { rarity: 'relic', guaranteed: false, pullable: false }
  ];

  // Merge the default rarities with database data
  const mergedRarities = defaultRarities.map((defaultRarity) => {
    const found = packageData.PackageRarities.find(
      (r) => r.rarity === defaultRarity.rarity
    );
    return found ? { ...defaultRarity, ...found } : defaultRarity;
  });

  return (
    <Container maxWidth="lg">
      {/* <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
        <img src={logo} alt="Logo" style={{ height: '250px' }} />
      </Box> */}

      <Grid container>
        <Grid
          item
          xs={12}
          md={6}
          textAlign={'center'}
          sx={{
            py: '2rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            component="img"
            src={packageData.imageUrl}
            alt={packageData.name}
            sx={{ width: '60%' }}
          />

          <Typography
            variant="h6"
            gutterBottom
            sx={{ mt: '1rem', mb: '0.5rem' }}
          >
            Available Rarities:
          </Typography>
          <Grid
            container
            spacing={1}
            sx={{ display: 'flex', justifyContent: 'center' }}
          >
            {mergedRarities.map((rarity, index) => (
              <Grid item key={index}>
                <Chip
                  label={`${rarity.rarity.charAt(0).toUpperCase()}${rarity.rarity.slice(1)}`}
                  color={
                    rarity.guaranteed
                      ? 'success'
                      : rarity.pullable
                        ? 'primary'
                        : 'default'
                  }
                  variant="outlined"
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          sx={{
            py: '2rem',
            height: '100%'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: '100%',
              justifyContent: 'center',
              overflow: 'auto'
            }}
            component={Paper}
          >
            {!showThankYou ? (
              <Box sx={{ p: 4 }}>
                {!showFinalUpsell && (
                  <form onSubmit={handleSubmit(onSubmit)}>
                    {/* Email Field + Enter Button */}
                    {(isUsernameValid === false ||
                      isUsernameValid === null) && (
                      <Controller
                        name="email"
                        control={control}
                        rules={{
                          required: true,
                          pattern: emailValidationRegex
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Email"
                            fullWidth
                            required
                            margin="normal"
                            autoComplete="email"
                            disabled={doesEmailExist !== null}
                            error={!!errors.email}
                            helperText={
                              errors.email && 'Please enter a valid email'
                            }
                          />
                        )}
                      />
                    )}

                    {doesEmailExist === null && (
                      <>
                        <Button
                          onClick={() => checkEmail(getValues('email'))}
                          disabled={
                            !getValues('email') ||
                            !emailValidationRegex.test(getValues('email'))
                          }
                          fullWidth
                          variant="contained"
                          sx={{ mt: '0.5rem' }}
                          size="large"
                        >
                          Enter
                        </Button>
                        <Button
                          variant="contained"
                          fullWidth
                          size="large"
                          color="error"
                          sx={{ mt: '1rem' }}
                          onClick={goBack}
                        >
                          Cancel
                        </Button>
                      </>
                    )}

                    {/* If email doesn't exist, show registration fields */}
                    {doesEmailExist === false &&
                      (isUsernameValid === false ||
                        isUsernameValid === null) && (
                        <>
                          <Controller
                            name="firstName"
                            control={control}
                            rules={{ required: true, validate: validateNameValue }}
                            render={({ field }) => (
                              <TextField
                                id="firstName"
                                margin="normal"
                                required
                                fullWidth
                                label="First Name"
                                error={Boolean(errors.firstName)}
                                helperText={
                                  errors.firstName &&
                                  ((errors.firstName.message as string) ||
                                    'Please enter your first name')
                                }
                                {...field}
                              />
                            )}
                          />

                          <Controller
                            name="lastName"
                            control={control}
                            rules={{ required: true, validate: validateNameValue }}
                            render={({ field }) => (
                              <TextField
                                id="lastName"
                                margin="normal"
                                required
                                fullWidth
                                label="Last Name"
                                error={Boolean(errors.lastName)}
                                helperText={
                                  errors.lastName &&
                                  ((errors.lastName.message as string) ||
                                    'Please enter your last name')
                                }
                                {...field}
                              />
                            )}
                          />

                          <Controller
                            name="username"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                              <TextField
                                id="username"
                                margin="normal"
                                required
                                fullWidth
                                label="Username"
                                error={
                                  Boolean(errors.username) ||
                                  isUsernameValid === false
                                }
                                helperText={errors.username?.message}
                                {...field}
                              />
                            )}
                          />

                          <CountrySelect
                            control={control}
                            onCountryChange={(areaCode) =>
                              setSelectedAreaCode(`+${areaCode}`)
                            }
                          />

                          <Controller
                            name="phone"
                            control={control}
                            rules={{
                              required: true,
                              minLength: {
                                value: 6, // Minimum length for the phone number
                                message:
                                  'Phone number must be at least 6 digits long'
                              },
                              maxLength: {
                                value: 15, // Maximum length for the phone number
                                message:
                                  'Phone number must be no more than 15 digits long'
                              },
                              pattern: {
                                value: /^[0-9]+$/, // Only allow numbers
                                message: 'Phone number must contain only digits'
                              }
                            }}
                            render={({ field }) => (
                              <TextField
                                id="phone"
                                margin="normal"
                                required
                                fullWidth
                                label="Phone"
                                error={Boolean(errors.phone)}
                                helperText={
                                  errors.phone && errors.phone.message
                                }
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      {selectedAreaCode}
                                    </InputAdornment>
                                  )
                                }}
                                {...field}
                              />
                            )}
                            disabled={!selectedAreaCode}
                          />

                          <Controller
                            name="state"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                              <TextField
                                id="state"
                                margin="normal"
                                required
                                fullWidth
                                label="State"
                                error={Boolean(errors.state)}
                                helperText={
                                  errors.state && 'Please enter your state'
                                }
                                {...field}
                              />
                            )}
                          />

                          <Controller
                            name="referralCode"
                            control={control}
                            rules={{ required: false }}
                            render={({ field }) => (
                              <TextField
                                margin="normal"
                                fullWidth
                                label="Referral Code"
                                id="referralCode"
                                {...field}
                              />
                            )}
                          />

                          <FormControlLabel
                            control={
                              <Checkbox
                                onChange={() =>
                                  setHasReadTermsAndConditions(
                                    !hasReadTermsAndConditions
                                  )
                                }
                              />
                            }
                            label={
                              <Typography>
                                I have read and agree to the{' '}
                                <Link
                                  href="https://incrypt-public-assets-global.s3.ap-southeast-2.amazonaws.com/documents/terms_and_conditions_05_08_2026.docx"
                                  rel="noopener noreferrer"
                                  variant="body2"
                                  target="_blank"
                                  style={{ fontSize: '16px' }}
                                >
                                  terms and conditions
                                </Link>
                              </Typography>
                            }
                            labelPlacement="end"
                            sx={{ mt: '1rem' }}
                          />
                          <FormControl sx={{ mt: '1rem' }}>
                            <FormLabel id="giveaway-Eligibility">
                              You have read the T&Cs and are eligible for
                              giveaways
                            </FormLabel>
                            <RadioGroup
                              onChange={(e) =>
                                setIsGiveawayEligible(e.target.value === 'yes')
                              }
                            >
                              <FormControlLabel
                                value="yes"
                                control={<Radio required={true} />}
                                label="Yes I am eligible for giveaways"
                              />
                              <FormControlLabel
                                value="no"
                                control={<Radio required={true} />}
                                label="No I am not eligible for giveaways"
                              />
                            </RadioGroup>
                          </FormControl>

                          <Button
                            variant="contained"
                            fullWidth
                            disabled={
                              !isValid ||
                              !hasReadTermsAndConditions ||
                              isGiveawayEligible === null
                            }
                            onClick={() => advanceToPayment()}
                            size="large"
                            sx={{ mt: '1rem' }}
                          >
                            Submit
                          </Button>

                          <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            color="error"
                            sx={{ mt: '1rem' }}
                            onClick={goBack}
                          >
                            Cancel
                          </Button>
                        </>
                      )}

                    {isUsernameValid && (
                      <Box>
                        {/* <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height={'100%'}
                          sx={{ width: '100%' }}
                        >
                          {isStandalone(packageData) && packageData.Bonus && (
                            <SpecialOfferCard>
                              <Typography
                                variant="h6"
                                gutterBottom
                                fontWeight="bold"
                              >
                                Special Offer
                              </Typography>
                              <Typography>
                                Gain extra {packageData.Bonus.extraCards} cards
                                and extra {packageData.Bonus.extraEntries}{' '}
                                entries into the giveaway.
                              </Typography>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={enableBonus}
                                    onChange={(e) =>
                                      setEnableBonus(e.target.checked)
                                    }
                                    name="enableBonus"
                                    color="primary"
                                    disabled={!!stripeLoading}
                                  />
                                }
                                label={`Add for ${packageData.Bonus.price}`}
                              />
                            </SpecialOfferCard>
                          )}
                        </Box> */}
                        <Box mt={2}>
                          <Typography
                            variant="h6"
                            gutterBottom
                            textAlign="center"
                            fontWeight="bold"
                            sx={{ pt: 2 }}
                          >
                            Order Summary
                          </Typography>
                          <List>
                            <ListItem>
                              <ListItemIcon>
                                <CircleIcon sx={{ fontSize: 16 }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={packageData.name}
                                secondary={`Price: A$${packageData.price}`}
                              />
                            </ListItem>
                            {isStandalone(packageData) &&
                              enableBonus &&
                              packageData.Bonus && (
                                <ListItem>
                                  <ListItemIcon>
                                    <CircleIcon sx={{ fontSize: 16 }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary="Special Offer"
                                    secondary={`Price: A$${packageData.Bonus.price}`}
                                  />
                                </ListItem>
                              )}
                          </List>
                          <Typography textAlign="end" fontWeight="bold">
                            Total Amount: A${totalAmount}
                          </Typography>
                        </Box>
                        <Box mt={2}>
                          <Typography
                            variant="h6"
                            gutterBottom
                            textAlign="center"
                            fontWeight="bold"
                            sx={{ pt: 2 }}
                          >
                            Payment Method
                          </Typography>
                          <PaymentElement
                            options={{ paymentMethodOrder: ['card'] }}
                          />
                        </Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={acceptTnC}
                              onChange={(e) => setAcceptTnC(e.target.checked)}
                              required
                            />
                          }
                          label="I accept the terms and conditions."
                        />
                        {message && (
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{
                              marginTop: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold'
                            }}
                          >
                            <ErrorOutline sx={{ marginRight: 1 }} />
                            {message}
                          </Typography>
                        )}
                        <Box mt={2}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            style={{ marginTop: '20px' }}
                            disabled={!stripe || stripeLoading}
                          >
                            {stripeLoading ? (
                              <CircularProgress size={24} />
                            ) : (
                              'Submit Payment'
                            )}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </form>
                )}

                {showFinalUpsell && (
                  <ExclusiveOfferInjected
                    paymentIntentId={paymentIntentIdAfterPayment}
                    handleCloseModal={() => setShowThankYou(true)}
                  />
                )}
              </Box>
            ) : (
              <Box sx={{ p: 4 }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ pb: 4 }}>
                    Thank you for your purchase. Please verify your email.
                  </Typography>

                  <Button
                    component={ReactRouterLink}
                    to="/login"
                    variant="contained"
                    fullWidth
                    size="large"
                  >
                    Login
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

const CheckoutWrapper = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const type = params.get('standalone')
    ? 'standalone'
    : params.get('subscription')
      ? 'subscription'
      : null;
  const name = params.get('standalone') || params.get('subscription');
  const mode = type === 'standalone' ? 'payment' : 'subscription';
  return (
    <Elements
      stripe={stripePromise}
      options={{
        mode: mode,
        amount: 200,
        currency: 'aud',
        paymentMethodTypes: ['card'],
        setupFutureUsage: 'on_session',
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: 'black',
            colorBackground: 'white',
            colorText: 'black'
          },
          rules: {
            '.Label': {
              color: 'white'
            }
          }
        }
      }}
    >
      <Checkout type={type} name={name} />
    </Elements>
  );
};

export default CheckoutWrapper;
